import { PlusIcon, TrashIcon } from '@radix-ui/react-icons';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { DataTable } from 'components/ui/data-table';
import { useModal } from 'hooks/useModal';
import { usePageCarto } from 'hooks/usePageCarto';
import { FC, useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { PageCartoIndicatorForm } from './PageCartoIndicatorForm';
import { useService } from 'hooks/useService';
import { ContentType } from 'services/abstract';
import { useToast } from 'hooks/useToast';

export const PageCartoIndicatorPanel: FC = () => {
  const { showModal, hideModal } = useModal();
  const queryClient = useQueryClient();
  const { pageCartoId, columns, indicators: dataGridRows } = usePageCarto();
  const indicatorService = useService(ContentType.INDICATOR);
  const { addToast } = useToast();

  const { mutateAsync: deleteIndicator } = useMutation({
    mutationFn: async (indicatorId: number) => {
      return await indicatorService.deleteIndicator(indicatorId);
    },
    onSuccess: () => {
      addToast({
        title: 'Indicateur supprimé',
        description: `Votre indicateur a été supprimé avec succès`,
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['page-carto', pageCartoId] });
    },
  });

  const tableColumns = useMemo<ColumnDef<(typeof dataGridRows)[number]>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Indicateur',
      },
      {
        accessorKey: 'description',
        header: 'Description',
      },
      {
        accessorKey: 'source',
        header: 'Source',
      },
      {
        accessorKey: 'validity',
        header: 'Validité',
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              icon={TrashIcon}
              size="sm"
              color="red"
              onClick={() => deleteIndicator(row.original.id)}
            />
          </div>
        ),
        enableHiding: false,
        enableSorting: false,
        meta: {
          className: 'w-24 text-right',
          cellClassName: 'text-right',
        },
      },
    ],
    [deleteIndicator]
  );

  const table = useReactTable({
    data: dataGridRows,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <div className="mt-5 text-right">
        <Button
          icon={PlusIcon}
          data-testid="create-indicator"
          onClick={() =>
            showModal({
              title: 'Nouvel Indicateur',
              Component: PageCartoIndicatorForm,
              props: { pageCartoId, columns, onSubmit: hideModal },
            })
          }
        >
          Nouvel Indicateur
        </Button>
      </div>
      <div className="my-2">
        {dataGridRows.length === 0 ? (
          <Alert variant="info">{`Aucun indicateur n'a été défini`}</Alert>
        ) : (
          <DataTable
            table={table}
            className="w-full max-h-96 overflow-y-auto"
          />
        )}
      </div>
    </div>
  );
};
